import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import GridLayout from 'react-grid-layout'
import { MdAdd } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { Layout } from 'react-grid-layout'
import type { ICatalogCategory } from '~/types/catalog'

import PostsPanel from './PostsPanel'

import Button from '~/components/Buttons/Button'
import SwitchButton from '~/components/Buttons/SwitchButton'
import CatalogCategory from '~/components/CatalogCategory'
import AddToModal from '~/components/CatalogModal'
import MwMLoader from '~/components/MwMLoader'
import ViewTitle from '~/components/ViewTitle'
import { SESSION_STORAGE } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import {
  keys as catalogKeys,
  useCatalogCategoriesListQuery,
  useGetLokalizeKeysQuery,
  useUpdateCategoriesPositionsMutation,
} from '~/queries/catalog'
import { handleGenericError } from '~/utils/error'

import './styles.scss'

const Categories = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { categoryId } = useParams()

  const [gridLayout, setGridLayout] =
    useState<{ category: ICatalogCategory; h: number; i: string; w: number; x: number; y: number }[]>()

  const [isNewElementAdded, setIsNewElementAdded] = useState(false)

  const [isAddToModalOpen, setIsAddToModalOpen] = useState(false)

  const [selectedModal, setSelectedModal] = useState<'category' | 'daily'>('category')

  const [selectedTemplate, setSelectedTemplate] = useState('')

  const [hasNavigated, setHasNavigated] = useState(false)

  const [isCompactModeSelected, setIsCompactModeSelected] = useState(false)

  /** Queries & Mutations */

  const { data: categories, isFetched, isLoading } = useCatalogCategoriesListQuery()

  const { mutateAsync: updateCategoriesPositions } = useUpdateCategoriesPositionsMutation()

  const selectedCategory = categories?.find(({ category_name }) => category_name === categoryId)

  const { data: lokaliseKeysData } = useGetLokalizeKeysQuery()

  /** Handlers */

  const handleCreateCategory = () => {
    setIsNewElementAdded(true)

    const newLayout = gridLayout?.map((el, index) => ({ ...el, i: el.category.category_name, y: index + 1 })) || []

    newLayout?.unshift({
      category: { category_name: 'new', enabled: false, lokalise_key: '', nb_translations: 0, position: 0 },
      h: 1,
      i: 'new',
      w: 12,
      x: 0,
      y: 0,
    })

    const layoutToSave = newLayout?.map(({ i, y }) => ({
      category_name: i,
      position: y,
    }))

    sessionStorage.setItem(SESSION_STORAGE.CATALOG_CATEGORIES_LAYOUT, JSON.stringify(layoutToSave))

    setGridLayout(newLayout)
  }

  const handleDragStop = async (layout: Layout[]) => {
    const formattedNewLayout = layout.map(({ i, y }) => ({
      category_name: i,
      position: Math.floor(y),
    }))

    if (isNewElementAdded) {
      sessionStorage.setItem(SESSION_STORAGE.CATALOG_CATEGORIES_LAYOUT, JSON.stringify(formattedNewLayout))
    } else {
      try {
        await updateCategoriesPositions({ positions: formattedNewLayout })
        await queryClient.invalidateQueries({ queryKey: catalogKeys.categories() })
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleExpand = (id: string) => () => {
    if (!isNewElementAdded) {
      if (categoryId === id) {
        setHasNavigated(true)
        navigate('/catalog/procolor/categories')
      } else {
        navigate(`/catalog/procolor/categories/${id}`)
      }
    }
  }

  const handleCancelCreate = () => {
    setIsNewElementAdded(false)
    setGridLayout(gridLayout?.filter(({ i }) => i !== 'new'))
  }

  const handleResetNewElement = () => setIsNewElementAdded(false)

  const handleSwitchModeChange = (prevState: boolean) => () => setIsCompactModeSelected(!prevState)

  const handleOpenAddToModal = (templateId: string, type: 'daily' | 'category') => {
    setIsAddToModalOpen(true)
    setSelectedModal(type)
    setSelectedTemplate(templateId)
  }

  const handleCloseAddToModal = () => setIsAddToModalOpen(false)

  /** Effects */

  useDocumentTitle('Catalog - Categories')

  useEffect(() => {
    if (categories) {
      setGridLayout(
        categories.map((category, index) => ({
          category: category,
          h: categoryId === category.category_name ? 2.35 : 1,
          i: category.category_name,
          w: 12,
          x: 0,
          y: index,
        }))
      )
    }
  }, [categories, categoryId])

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isFetched && !hasNavigated && categories && categories.length > 0 && !selectedCategory) {
      timeout = setTimeout(
        () => navigate(`/catalog/procolor/categories/${categories[0].category_name}`, { replace: true }),
        100
      )
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [categories, hasNavigated, selectedCategory, isFetched, navigate])

  /** Render */

  return (
    <div className="catalog-categories-view">
      <ViewTitle className="catalog-categories-view__title-container">
        Categories
        <SwitchButton
          buttons={[
            { content: 'Detailed', id: 'detailed', isActive: !isCompactModeSelected },
            { content: 'Compact', id: 'compact', isActive: isCompactModeSelected },
          ]}
          onButtonSelectionChange={handleSwitchModeChange(isCompactModeSelected)}
        />
      </ViewTitle>
      <div className="catalog-categories-view__content">
        {isLoading ? (
          <MwMLoader category="item" />
        ) : (
          <>
            <div className="catalog-categories-view__content__left-section">
              <div className="catalog-categories-view__content__left-section__create-wrapper">
                {isNewElementAdded ? (
                  <Button
                    className="catalog-categories-view__content__left-section__create-wrapper__cancel-button"
                    onClick={handleCancelCreate}
                  >
                    CANCEL
                  </Button>
                ) : (
                  <Button disabled={isNewElementAdded} onClick={handleCreateCategory}>
                    <MdAdd color="#fff" />
                    Create a category
                  </Button>
                )}
              </div>
              <GridLayout
                className="catalog-categories-view__content__left-section__categories-wrapper"
                containerPadding={[0, 0]}
                draggableHandle=".move-button"
                isResizable={false}
                layout={gridLayout}
                onDragStop={handleDragStop}
                rowHeight={60}
                useCSSTransforms={false}
              >
                {gridLayout?.map((layout) => (
                  <div
                    className="catalog-categories-view__content__left-section__categories-wrapper__grid-item"
                    key={layout.i}
                  >
                    <CatalogCategory
                      category={layout.category}
                      handleResetNewElement={handleResetNewElement}
                      isAddingElement={isNewElementAdded}
                      isMatchParams={categoryId === layout.category.category_name}
                      isNewElement={isNewElementAdded && layout.category.category_name === 'new'}
                      lokaliseKeys={lokaliseKeysData || []}
                      onExpand={handleExpand(layout.category.category_name)}
                    />
                  </div>
                ))}
              </GridLayout>
            </div>
            <PostsPanel
              categoryId={categoryId}
              isCompactModeSelected={isCompactModeSelected}
              onOpenAddToCategoryModal={(coloringTemplateId) => handleOpenAddToModal(coloringTemplateId, 'category')}
              onOpenAddToDailyModal={(coloringTemplateId) => handleOpenAddToModal(coloringTemplateId, 'daily')}
            />
            <AddToModal
              isOpen={isAddToModalOpen}
              onClose={handleCloseAddToModal}
              selectedTemplate={selectedTemplate}
              type={selectedModal}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Categories
